/**
 * Pure helpers for the student E2EE overlay lock (Phase 5). Kept separate from
 * Class.svelte so they are unit-testable without a Svelte runtime.
 */

/** Visual disposition of the overlay lock icon. */
export type E2eeOverlayState =
  | "unavailable" // client can't do E2EE (isE2EESupported() === false)
  | "forced" // device forces E2EE — toggle locked/disabled
  | "rekey-needed" // session needs a key we don't have (rotated/late-join)
  | "encrypted" // live per-frame decryption ON (togglable)
  | "plain"; // live per-frame decryption OFF (togglable)

export interface OverlayInputs {
  /** Global E2EE flag (server-reported). */
  enabled: boolean;
  /** Runtime isE2EESupported() of this client. */
  clientSupports: boolean;
  /** Negotiated token state: device forces E2EE (opt-out locked). */
  forced: boolean;
  /** Live decryption state (client.isE2EEEnabled()). */
  encrypted: boolean;
  /**
   * Phase 6: the negotiated session wants E2EE active but this client has NO
   * usable media key for it (rotation made the stored key stale, or a late join
   * before scanning). When true the overlay surfaces a "re-scan QR" prompt.
   * Defaults false → behaves exactly as Phase 5.
   */
  staleKey?: boolean;
}

/**
 * Map the current E2EE inputs to the overlay icon disposition. Precedence:
 * unavailable (no capability) > rekey-needed (active session, no usable key) >
 * forced (locked) > live encrypted/plain.
 *
 * `rekey-needed` ranks above `forced` so a forced session with a stale key
 * tells the student to re-scan (the actionable state) rather than just showing
 * the locked icon.
 */
export function overlayState(inputs: OverlayInputs): E2eeOverlayState {
  if (!inputs.enabled || !inputs.clientSupports) return "unavailable";
  if (inputs.staleKey === true && !inputs.encrypted) return "rekey-needed";
  if (inputs.forced) return "forced";
  return inputs.encrypted ? "encrypted" : "plain";
}

/** Whether the overlay toggle is interactive. Disabled when forced/unavailable/rekey. */
export function overlayToggleEnabled(state: E2eeOverlayState): boolean {
  return state === "encrypted" || state === "plain";
}
